import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Building2,
  ShieldCheck,
  Rocket,
  Share2,
  Copy,
  Users,
  BookOpenCheck,
  TrendingUp,
  Bell,
  Sparkles,
} from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";
import ShareBar from "../components/landing/ShareBar";
import { toast } from "sonner";

/**
 * /companies — sister campaign page to /sole-traders, aimed at 2-owner
 * companies, husband-wife businesses and growing partnerships. Every CTA
 * routes into the /enrol/company iframe wrapper, which loads the ClientOps
 * enrolment form under the nztaxation.nz domain.
 */
const ENROL = "/enrol/company";

export default function CompaniesPage() {
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/companies`
      : "https://nztaxation.nz/companies";
  const shareTitle = "Company accounting done right — NZ Taxation Limited";
  const shareDescription =
    "For 2-owner NZ companies: Xero-first bookkeeping, GST + income tax, shareholder returns, and monthly clarity on where your business really stands.";

  const shareThisPage = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareDescription, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied — share it with a fellow business owner");
      }
    } catch {
      /* cancelled */
    }
  };

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] text-[#0F172A]"
      data-testid="companies-page"
    >
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="absolute -top-40 -left-40 w-[36rem] h-[36rem] bg-[#C9A349]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-14 md:pb-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="companies-back"
          >
            <ArrowLeft className="w-3 h-3" /> nztaxation.nz
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-700 bg-white/70 backdrop-blur"
          >
            <Sparkles className="w-3 h-3 text-[#C9A349]" />
            NZ Limited Companies · Husband-wife businesses · Partnerships
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.98]"
            data-testid="companies-headline"
          >
            Structure your company <br />
            <span className="text-[#C9A349]">for growth. Not just compliance.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed"
          >
            You run a company because you&apos;re aiming higher than a side hustle. Your
            accountant should treat it the same way. At NZ Taxation we handle the
            <b> full company stack</b> — Xero setup, GST, income tax, shareholder salaries,
            LTC decisions and month-by-month visibility so you always know where the
            business really stands.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link
              to={ENROL}
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors"
              data-testid="companies-cta-hero"
              title="NZ Taxation Limited | Company enrolment"
            >
              <Rocket className="w-4 h-4" /> Enrol your company
            </Link>
            <button
              type="button"
              onClick={() => (window.location.href = "#why-switch")}
              className="inline-flex items-center gap-2 border border-slate-300 px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:border-[#C9A349] hover:text-[#0A192F] transition-colors"
              data-testid="companies-secondary-cta"
            >
              Why switch to us <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> Xero-certified advisors
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> IRD-registered tax agent
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> Fully remote — Auckland based
            </span>
          </div>
        </div>
      </section>

      {/* Why switch */}
      <section id="why-switch" className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            Why companies switch
          </div>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            The full company stack — <br />
            <span className="text-[#C9A349]">handled properly.</span>
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <BenefitCard
              icon={BookOpenCheck}
              title="Xero-first company bookkeeping"
              body="We set your Xero file up properly from day one — chart of accounts, tax rates, bank feeds, payroll linked. No more end-of-year clean-up bills because we kept it tidy all year."
            />
            <BenefitCard
              icon={ShieldCheck}
              title="GST + IR4 + shareholder IR3s"
              body="Company tax return (IR4), 2-monthly or 6-monthly GST, and each shareholder's personal IR3 — all filed by the same team so nothing falls between the cracks."
            />
            <BenefitCard
              icon={Users}
              title="Shareholder salary decisions"
              body="How much to draw as salary vs dividend vs shareholder current account? We model it against your marginal rate every year and give you the number that keeps the tax bill lowest — legally."
            />
            <BenefitCard
              icon={TrendingUp}
              title="Monthly financial clarity"
              body="Every month you get a plain-English snapshot of profit, cash, tax coming, and where the business is trending. No more waiting nine months to find out you had a great year."
            />
            <BenefitCard
              icon={Bell}
              title="Deadline calendar built for companies"
              body="GST due dates, provisional tax instalments, PAYE, annual return, terminal tax — we run the calendar so you can run the business."
              wide
            />
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="py-20 md:py-28 bg-[#0A192F] text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute top-1/2 -right-40 w-[36rem] h-[36rem] bg-[#C9A349]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#C9A349]">
            Who this is for
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            Built for 2-owner Kiwi companies <br />
            <span className="text-[#C9A349]">that mean business.</span>
          </h2>
          <p className="mt-5 text-slate-300 text-lg max-w-2xl leading-relaxed">
            Whether you&apos;re a husband-wife shop, a partnership that just incorporated,
            a growing trade business, or a two-founder services company — you need
            answers to:
          </p>

          <ul className="mt-8 grid md:grid-cols-2 gap-3 text-slate-100">
            {[
              "Is our current structure still the right one?",
              "How much can we safely take out this month?",
              "Are we paying more tax than we should?",
              "What's the company's real profit — not the number in Xero?",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 border-l-2 border-[#C9A349] pl-4 py-1">
                <CheckCircle2 className="w-5 h-5 text-[#C9A349] flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 border-l-4 border-[#C9A349] pl-5 text-slate-200 max-w-2xl">
            <p className="font-heading font-black text-xl md:text-2xl leading-snug">
              A company that knows its numbers <br />
              is a company that grows on purpose.
            </p>
          </div>
        </div>
      </section>

      {/* Referral block */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            Community referral
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight">
            Know a company that&apos;s outgrown <br />
            <span className="text-[#C9A349]">their current accountant?</span>
          </h2>
          <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed">
            Most switching happens because owners feel unheard, unadvised or blindsided at
            year-end. A quick introduction from you could give another Kiwi business the
            clarity they&apos;ve been missing.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={shareThisPage}
              className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-6 py-4 font-heading font-black uppercase tracking-[0.14em] text-sm hover:bg-[#0A192F] hover:text-white transition-colors"
              data-testid="companies-refer-btn"
            >
              <Share2 className="w-4 h-4" /> Share this page
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Link copied to clipboard");
                } catch {
                  toast.error("Couldn't copy — please copy the URL manually");
                }
              }}
              className="inline-flex items-center gap-2 border border-slate-300 px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:border-[#C9A349] transition-colors"
              data-testid="companies-copy-link"
            >
              <Copy className="w-4 h-4" /> Copy page link
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0A192F] text-white bg-grid-dark">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#C9A349]">
            Ready to switch?
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-6xl tracking-tight leading-[1.02]">
            Get your company on <br />
            <span className="text-[#C9A349]">the right footing.</span>
          </h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto text-lg">
            Under five minutes to enrol. We&apos;ll book a welcome call within 24 hours and
            take the reins from your current accountant with zero drama on your end.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to={ENROL}
              className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-8 py-5 font-heading font-black uppercase tracking-[0.14em] text-base hover:bg-white transition-colors"
              data-testid="companies-cta-final"
            >
              <Building2 className="w-5 h-5" /> Enrol your company <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/sole-traders"
              className="inline-flex items-center gap-2 border border-white/25 text-white px-8 py-5 font-heading font-bold uppercase tracking-[0.14em] text-base hover:bg-white/10 transition-colors"
              data-testid="companies-switch-sole"
            >
              I&apos;m a sole trader instead <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <ShareBar url={url} title={shareTitle} description={shareDescription} />
      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}

function BenefitCard({ icon: Icon, title, body, wide }) {
  return (
    <div
      className={`border border-slate-200 bg-white p-7 md:p-8 flex gap-5 items-start hover:-translate-y-1 hover:border-[#C9A349] transition-all ${
        wide ? "md:col-span-2" : ""
      }`}
    >
      <div className="w-11 h-11 flex items-center justify-center bg-[#0A192F] text-[#C9A349] flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="font-heading font-black text-lg md:text-xl text-[#0A192F] tracking-tight">
          {title}
        </h3>
        <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
